import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { accountService } from '../../services/accountService';
import  categoryService  from '../../services/categoryService';
import { CloseOutlined } from '@ant-design/icons';

const { Option } = Select;

const AccountCreateForm = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [divisionLoading, setDivisionLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      loadDivisionOptions();
    }
  }, [visible, form]);

  const loadDivisionOptions = async () => {
    try {
      setDivisionLoading(true);
      const response = await categoryService.listDivision();
      if (response.success) {
        setDivisionOptions(response.data);
      }
    } catch (error) {
      console.error('获取事业部列表失败:', error);
      message.error(t('account.fetchDivisionFailed') || '获取事业部列表失败');
    } finally {
      setDivisionLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // 构造API请求数据
      const accountData = {
        ...values,
        passwordType: "0", // 固定值
        orgCode: values.division, // 将division值作为orgCode传递
        orgName: divisionOptions.find(option => option.value === values.division)?.label, // 获取对应的label作为orgName
        sourceCode: 0
      };
      
      // 移除division字段，因为我们只需要orgCode和orgName
      delete accountData.division;
      
      const response = await accountService.createAccount(accountData);
      
      if (response.success) {
        message.success(t('account.createSuccess'));
        onSuccess();
      } else {
        message.error(response.errorMessage || t('account.createFailed'));
      }
    } catch (error) {
      console.error('创建账号失败:', error);
      message.error(error.mgessage || t('account.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{t('account.createTitle')}</span>
      <CloseOutlined onClick={onCancel} style={{ cursor: 'pointer' }} />
    </div>
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      destroyOnClose
      width={600}
      closable={false}
      className="account-create-modal"
      footer={[
        <div key="footer" style={{ textAlign: 'right', padding: '10px 0' }}>
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={loading}
          >
            {t('common.submit')}
          </button>
        </div>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="loginId"
          label={<span className="required-label">{t('account.account')}</span>}
          rules={[
            { required: true, message: t('account.accountRequired') },
            { min: 6, max: 20, message: t('account.accountLengthRequired') }
          ]}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item>

        <Form.Item
          name="password"
          label={<span className="required-label">{t('account.password')}</span>}
          rules={[
            { required: true, message: t('account.passwordRequired') },
            { min: 8, max: 20, message: t('account.passwordLengthRequired') },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,20}$/,
              message: t('account.passwordComplexityRequired')
            }
          ]}
        >
          <Input.Password placeholder={t('common.pleaseInput')} maxLength={20} />
        </Form.Item>

        <Form.Item
          name="name"
          label={<span className="required-label">{t('account.name')}</span>}
          rules={[
            { required: true, message: t('account.nameRequired') },
            {
              pattern: /^[^!@#$%^&*()]+$/,
              message: t('account.nameSpecialCharNotAllowed') || '名字不能包含特殊字符 !@#$%^&*()'
            }
          ]}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item>

        <Form.Item
          name="division"
          label={<span className="required-label">{t('account.division')}</span>}
          rules={[{ required: true, message: t('account.divisionRequired') }]}
        >
          <Select 
            placeholder={t('account.divisionPlaceholder') || t('common.pleaseSelect')}
            loading={divisionLoading}
            allowClear
          >
            {divisionOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="email"
          label={t('account.email')}
          rules={[
            { 
              type: 'email',
              message: t('account.emailInvalid')
            }
          ]}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item>

        {/* <Form.Item
          name="mobile"
          label={t('account.mobile')}
        >
          <Input placeholder={t('common.pleaseInput')} />
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

export default AccountCreateForm;